import { useState, useEffect, useCallback, useRef } from 'react';
import { Drawer, Form, Select, Button, Row, Col, message, Modal, Tooltip, Upload, Image as AntdImage, Input } from 'antd';
// import { UploadOutlined, CloseCircleOutlined } from '@ant-design/icons';
import html2canvas from 'html2canvas';
import goldMedal from '@/static/images/rank_gold@2x.png';
import silverMedal from '@/static/images/rank_silver@2x.png';
import copperMedal from '@/static/images/rank_copper@2x.png';
import './index.less';
import config from '@config';
import { fileApi } from '@services/Conference';
import { getCaseScoreList, getH5RankingSetting, getScoreStageByIdApi, getScoreTopicsListApi } from '@services/Selection';
import { prepareBCOSSUrl } from '@utils/utils';

const colorList = ['#f5222d', '#fa541c', '#fa8c16', '#52c41a', '#1677ff', '#0327ff', '#722ed1', '#eb2f96', '#333333', '#888888', '#ffffff'].map(c => c.toUpperCase());

const ExportH5RankingDrawer = ({ open, onClose, title = '导出H5榜单', projectInfo = {} }) => {
    const [form] = Form.useForm();
    const [styleForm] = Form.useForm();
    const [styleModalVisible, setStyleModalVisible] = useState(false);
    const [topicList, setTopicList] = useState([]);
    const [topicLoading, setTopicLoading] = useState(false);
    const [styleSettings, setStyleSettings] = useState({
        bg_image_url: '',
        bg_color: '',
        topic_title_bg_img_url: '',
        table_head_bg_color: '',
        awards_bg_img_url: '',
    });
    const [hasStyleSettings, setHasStyleSettings] = useState(true);
    const [h5BackgroundUrlList, setH5BackgroundUrlList] = useState([]);
    const [h5TopicTitleBgUrlList, setH5TopicTitleBgUrlList] = useState([]);
    const [h5AwardsBgUrlList, setH5AwardsBgUrlList] = useState([]);
    const [dataLoading, setDataLoading] = useState(false);
    const [rankingData, setRankingData] = useState([]);
    const [stageName, setStageName] = useState('');
    const isAllowCheck = useRef(null);
    const optionHeaders = { headers: { 'Content-Type': 'multipart/form-data' } };

    const topicId = Form.useWatch('topicId', form);

    // 获取已选主题名称（分隔符之后的内容）
    const getSelectedTopicName = () => {
        if (topicId && topicList.length > 0) {
            const selectedTopic = topicList.find(item => item.value === topicId);
            if (selectedTopic) {
                // 提取分隔符之后的内容
                const match = selectedTopic.label.match(/[ 　:：\-—](.+)$/);
                return match ? match[1] : selectedTopic.label;
            }
        }
        return '';
    };

    // 根据正则提取标题文字（分隔符之前的内容）
    const extractTitleText = () => {
        if (topicId && topicList.length > 0) {
            const selectedTopic = topicList.find(item => item.value === topicId);
            if (selectedTopic) {
                // 提取分隔符之前的内容
                const match = selectedTopic.label.match(/^(.+?)[ 　:：\-—]/);
                return match ? match[1] : selectedTopic.label;
            }
        }
        return '';
    };

    // 根据奖项名称分组数据
    const groupDataByAward = () => {
        const groups = {};
        rankingData.forEach(item => {
            const awardName = item.rewardName || '未分组';
            if (!groups[awardName]) {
                groups[awardName] = [];
            }
            groups[awardName].push(item);
        });
        return Object.entries(groups).map(([awardName, data]) => ({
            awardName,
            data,
        }));
    };

    // 提取背景图底部最后一像素的颜色
    const extractBackgroundImageBottomColor = useCallback(imageUrl => {
        return new Promise(resolve => {
            if (!imageUrl) {
                resolve('');
                return;
            }
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                try {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    // 获取底部最后一行像素中间位置的颜色
                    const imageData = ctx.getImageData(Math.floor(img.width / 2), img.height - 1, 1, 1);
                    const [r, g, b] = imageData.data;
                    const color = `rgb(${r}, ${g}, ${b})`;
                    resolve(color);
                } catch (error) {
                    console.error('Failed to extract image color:', error);
                    resolve('');
                }
            };
            img.onerror = () => {
                console.error('Failed to load background image');
                resolve('');
            };
            img.src = imageUrl;
        });
    }, []);

    // 当样式设置更新时，提取背景图底部颜色
    useEffect(() => {
        if (styleSettings.bg_image_url) {
            const imageUrl = prepareBCOSSUrl(styleSettings.bg_image_url);
            extractBackgroundImageBottomColor(imageUrl).then(color => {
                if (color) {
                    setStyleSettings(prev => ({
                        ...prev,
                        bg_color_bottom: color,
                    }));
                }
            });
        }
    }, [styleSettings.bg_image_url, extractBackgroundImageBottomColor]);

    // 当Drawer关闭时清空数据
    useEffect(() => {
        if (!open) {
            form.resetFields();
            setRankingData([]);
        }
    }, [open, form]);

    // 从接口加载主题列表
    useEffect(() => {
        if (open && projectInfo?.projectId) {
            setTopicLoading(true);
            getScoreTopicsListApi({ projectId: projectInfo.projectId, stageId: projectInfo.stageId })
                .then(res => {
                    if (res?.status_code === 200 && res.data) {
                        const topics = res.data.map(item => ({
                            label: item.topicName,
                            value: item.topicId,
                        }));
                        setTopicList(topics);
                    }
                })
                .catch(error => {
                    console.error('Failed to load topics:', error);
                    message.error('加载主题列表失败');
                })
                .finally(() => {
                    setTopicLoading(false);
                });
        }
    }, [open, projectInfo?.projectId]);

    // 加载榜单数据
    useEffect(() => {
        if (topicId && projectInfo?.projectId && projectInfo?.stageId) {
            setDataLoading(true);
            getCaseScoreList({
                projectId: projectInfo.projectId,
                stageId: projectInfo.stageId,
                topicId: topicId,
                platformType: 'H5',
            })
                .then(res => {
                    if (res?.status_code === 200 && res.data?.list) {
                        const formattedData = res.data.list.map(item => ({
                            key: item.caseId,
                            caseNo: item.caseId,
                            caseName: item.caseName,
                            submissionUnit: item.hosName || '提报单位',
                            rewardName: item.rewardName || '',
                        }));
                        setRankingData(formattedData);
                    } else {
                        setRankingData([]);
                    }
                })
                .catch(error => {
                    console.error('Failed to load ranking data:', error);
                    setRankingData([]);
                })
                .finally(() => {
                    setDataLoading(false);
                });
        } else {
            setRankingData([]);
        }
    }, [projectInfo?.projectId, projectInfo?.stageId, topicId]);

    const handleDownloadImage = useCallback(async () => {
        const previewElement = document.querySelector('.h5-preview-section');
        if (!previewElement) {
            message.error('预览区域未找到');
            return;
        }

        try {
            message.loading({ content: '正在生成图片...', key: 'downloadImage' });
            // 等待背景图资源加载完成
            await new Promise(resolve => setTimeout(resolve, 500));

            const canvas = await html2canvas(previewElement, {
                useCORS: true,
                allowTaint: true,
                backgroundColor: null,
                scale: 2,
            });

            const link = document.createElement('a');
            link.href = canvas.toDataURL('image/png');
            // 生成文件名: 赛段_主题名称.png (使用完整的主题名称)
            const selectedTopic = topicList.find(t => t.value === topicId);
            const fullTopicName = selectedTopic?.label || '';
            const fileName = `${stageName}_${fullTopicName}.png`;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            message.success({ content: '图片下载成功', key: 'downloadImage' });
        } catch (error) {
            message.error({ content: '图片下载失败: ' + error.message, key: 'downloadImage' });
            console.error('Download error:', error);
        }
    }, [projectInfo, stageName, topicId, topicList]);

    useEffect(() => {
        if (open && projectInfo?.stageId) {
            getScoreStageByIdApi({ id: projectInfo.stageId })
                .then(res => {
                    if (res?.status_code === 200 && res.data) {
                        const stageData = res.data;
                        // 获取赛段名称
                        setStageName(stageData.name || '');
                        let settings = {
                            bg_image_url: '',
                            bg_color: '',
                            topic_title_bg_img_url: '',
                            table_head_bg_color: '',
                            awards_bg_img_url: '',
                        };

                        // 解析rankStyleSetting中的H5样式设置
                        if (stageData.rankStyleSetting) {
                            try {
                                const rankSettings = JSON.parse(stageData.rankStyleSetting);
                                if (rankSettings.h5) {
                                    settings = {
                                        ...settings,
                                        ...rankSettings.h5,
                                    };
                                    setHasStyleSettings(true);
                                } else {
                                    setHasStyleSettings(false);
                                }
                            } catch (error) {
                                console.error('Failed to parse rank style settings:', error);
                                setHasStyleSettings(false);
                            }
                        } else {
                            setHasStyleSettings(false);
                        }

                        setStyleSettings(settings);
                    }
                })
                .catch(error => {
                    console.error('Failed to load stage info:', error);
                    setHasStyleSettings(false);
                });
        }
    }, [open, projectInfo?.stageId]);

    const openStyleModal = () => {
        const formValues = {
            ...styleSettings,
            bg_color: styleSettings.bg_color || '',
        };
        styleForm.setFieldsValue(formValues);
        if (styleSettings.bg_image_url) {
            setH5BackgroundUrlList([prepareBCOSSUrl(styleSettings.bg_image_url)]);
        }
        if (styleSettings.topic_title_bg_img_url) {
            setH5TopicTitleBgUrlList([prepareBCOSSUrl(styleSettings.topic_title_bg_img_url)]);
        }
        if (styleSettings.awards_bg_img_url) {
            setH5AwardsBgUrlList([prepareBCOSSUrl(styleSettings.awards_bg_img_url)]);
        }
        setStyleModalVisible(true);
    };

    const handleStyleModalOk = async () => {
        try {
            // 先验证表单
            await styleForm.validateFields();
            const values = styleForm.getFieldsValue();
            const settingJson = JSON.stringify({
                bg_image_url: values.bg_image_url || '',
                bg_color: styleSettings.bg_color_bottom || '',
                topic_title_bg_img_url: values.topic_title_bg_img_url || '',
                table_head_bg_color: values.table_head_bg_color || '',
                awards_bg_img_url: values.awards_bg_img_url || '',
            });

            const apiParams = {
                settingJson,
                stageId: projectInfo.stageId,
            };

            message.loading({ content: '样式设置保存中...', key: 'styleModalSave' });
            const res = await getH5RankingSetting(apiParams);

            if (res?.status_code === 200) {
                const newSettings = {
                    ...styleSettings,
                    ...values,
                };
                setStyleSettings(newSettings);
                setStyleModalVisible(false);
                message.success({ content: '样式设置保存成功', key: 'styleModalSave' });
                setHasStyleSettings(true);
            }
        } catch (error) {
            console.error('Save style settings error:', error);
        }
    };

    const changeBgColor = (color, fieldName) => {
        styleForm.setFieldValue(fieldName, color);
    };

    // 文件校验
    const checkout = (file, size) => {
        const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg';
        if (!isJpgOrPng) {
            message.error('仅支持上传JPG/PNG格式的图片');
            isAllowCheck.current = true;
            return false;
        } else {
            isAllowCheck.current = false;
        }

        const isLtSize = file.size / 1024 / 1024 < size;
        if (!isLtSize) {
            message.error(`图片不能大于${size}MB`);
            isAllowCheck.current = true;
            return false;
        } else {
            isAllowCheck.current = false;
        }

        return isJpgOrPng && isLtSize;
    };

    // 通用背景图上传函数
    const uploadBackground = (info, setUrlList, fieldName) => {
        const formData = new FormData();
        formData.append('file', info.file);
        formData.append('type', 14);
        if (!isAllowCheck.current) {
            fileApi(formData, optionHeaders).then(res => {
                if (!res) {
                    message.error('文件上传失败');
                    return false;
                }
                if (res.status_code === 200) {
                    setUrlList([prepareBCOSSUrl(res.data)]);
                    styleForm.setFieldValue(fieldName, res.data);
                }
            });
        } else {
            styleForm.setFieldValue(fieldName, undefined);
        }
    };

    // 通用删除背景图函数
    const removeBackground = (setUrlList, fieldName) => {
        setUrlList([]);
        styleForm.setFieldValue(fieldName, '');
    };

    // H5背景图上传
    const uploadH5Background = info => uploadBackground(info, setH5BackgroundUrlList, 'bg_image_url');
    const uploadH5TopicTitleBg = info => uploadBackground(info, setH5TopicTitleBgUrlList, 'topic_title_bg_img_url');
    const uploadH5AwardsBg = info => uploadBackground(info, setH5AwardsBgUrlList, 'awards_bg_img_url');

    // 删除H5背景图
    const removeH5Background = () => removeBackground(setH5BackgroundUrlList, 'bg_image_url');
    const removeH5TopicTitleBg = () => removeBackground(setH5TopicTitleBgUrlList, 'topic_title_bg_img_url');
    const removeH5AwardsBg = () => removeBackground(setH5AwardsBgUrlList, 'awards_bg_img_url');

    const renderColorField = (fieldName, label) => {
        return (
            <Form.Item className="required" label={label} rules={[{ required: true, message: `${label}必填` }]}>
                <div className="color-field-wrap">
                    <div className="color-field">
                        <Form.Item noStyle shouldUpdate>
                            {({ getFieldValue }) => {
                                const color = getFieldValue(fieldName);
                                return !!color && <div className="color-previewer" style={{ backgroundColor: `${color}`, borderColor: '#CCCCCC' }} />;
                            }}
                        </Form.Item>

                        <Form.Item
                            noStyle
                            name={fieldName}
                            rules={[
                                { required: true, message: `${label}必填` },
                                { pattern: /^#[0-9a-f]{6}$/i, message: '色值不符合格式要求' },
                            ]}
                        >
                            <Input style={{ width: 120, border: 'none', boxShadow: 'none' }} maxLength={7} placeholder="#FFFFFF" />
                        </Form.Item>
                    </div>
                    <div className="color-list">
                        {colorList.map((item, index) => (
                            <Tooltip key={index} placement="top" title="点击使用该颜色">
                                <div onClick={() => styleForm.setFieldValue(fieldName, item)} className="color-previewer" style={{ backgroundColor: `${item}`, borderColor: '#CCCCCC' }} />
                            </Tooltip>
                        ))}
                    </div>
                </div>
            </Form.Item>
        );
    };

    const renderStyleForm = () => {
        return (
            <>
                <Form.Item name="bg_image_url" label="背景图" extra="支持JPG/PNG格式，大小不超过5MB，推荐尺寸1500*1800px">
                    {!!h5BackgroundUrlList.length && (
                        <div className="background-image-wrap">
                            <AntdImage width={200} src={h5BackgroundUrlList[0]} style={{ marginBottom: 8 }} />
                            <i className="bi bi-x-circle image-close-icon" onClick={removeH5Background} />
                        </div>
                    )}
                    <div>
                        <Upload beforeUpload={file => checkout(file, 5)} fileList={h5BackgroundUrlList} customRequest={uploadH5Background} maxCount={1} showUploadList={false}>
                            <Button icon={<i className="bi bi-upload" />}>{!h5BackgroundUrlList.length ? '上传图片' : '更改图片'}</Button>
                        </Upload>
                    </div>
                </Form.Item>
                <Form.Item name="topic_title_bg_img_url" label="主题背景图" extra="支持JPG/PNG格式，大小不超过5MB，推荐尺寸1000*180px">
                    {!!h5TopicTitleBgUrlList.length && (
                        <div className="background-image-wrap">
                            <AntdImage width={200} src={h5TopicTitleBgUrlList[0]} style={{ marginBottom: 8 }} />
                            <i className="bi bi-x-circle image-close-icon" onClick={removeH5TopicTitleBg} />
                        </div>
                    )}
                    <div>
                        <Upload beforeUpload={file => checkout(file, 5)} fileList={h5TopicTitleBgUrlList} customRequest={uploadH5TopicTitleBg} maxCount={1} showUploadList={false}>
                            <Button icon={<i className="bi bi-upload" />}>{!h5TopicTitleBgUrlList.length ? '上传图片' : '更改图片'}</Button>
                        </Upload>
                    </div>
                </Form.Item>
                {renderColorField('table_head_bg_color', '表头单元格背景色')}
                <Form.Item name="awards_bg_img_url" label="荣誉名称背景图" extra="支持JPG/PNG格式，大小不超过5MB，推荐尺寸880*140px">
                    {!!h5AwardsBgUrlList.length && (
                        <div className="background-image-wrap">
                            <AntdImage width={200} src={h5AwardsBgUrlList[0]} style={{ marginBottom: 8 }} />
                            <i className="bi bi-x-circle image-close-icon" onClick={removeH5AwardsBg} />
                        </div>
                    )}
                    <div>
                        <Upload beforeUpload={file => checkout(file, 5)} fileList={h5AwardsBgUrlList} customRequest={uploadH5AwardsBg} maxCount={1} showUploadList={false}>
                            <Button icon={<i className="bi bi-upload" />}>{!h5AwardsBgUrlList.length ? '上传图片' : '更改图片'}</Button>
                        </Upload>
                    </div>
                </Form.Item>
            </>
        );
    };

    return (
        <>
            <Drawer
                closable={{ placement: 'end' }}
                title={title}
                placement="bottom"
                onClose={onClose}
                open={open}
                size="100vh"
                destroyOnHidden
                styles={{
                    body: {
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                    },
                }}
                footer={null}
            >
                <div className="export-ranking-content">
                    <Form form={form}>
                        <Row gutter={16} align="middle" className="form-control-row">
                            <Col span={8}>
                                <Form.Item name="topicId" label="主题：" rules={[{ required: true, message: '请选择主题' }]} className="form-item-no-margin">
                                    <Select placeholder="请选择主题" options={topicList} loading={topicLoading} />
                                </Form.Item>
                            </Col>
                            <Col span={16} className="button-group">
                                <Button type="primary" onClick={openStyleModal}>
                                    样式设置
                                </Button>
                                <Button type="primary" onClick={handleDownloadImage}>
                                    下载H5榜单图
                                </Button>
                            </Col>
                        </Row>
                    </Form>

                    <div className="content-divider" />

                    <div
                        className="h5-preview-section"
                        style={{
                            backgroundImage: styleSettings.bg_image_url ? `url(${prepareBCOSSUrl(styleSettings.bg_image_url)})` : 'none',
                            backgroundColor: styleSettings.bg_color || styleSettings.bg_color_bottom || 'transparent',
                        }}
                    >
                        {!hasStyleSettings ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '500px', fontSize: '18px', color: '#666', fontWeight: '500' }}>
                                <div style={{ textAlign: 'center' }}>
                                    <div>请先完成样式设置</div>
                                    <div style={{ fontSize: '14px', color: '#999', marginTop: '8px' }}>点击上方"样式设置"按钮进行配置</div>
                                </div>
                            </div>
                        ) : (
                            <div className="ranking-table-wrapper">
                                <div className="ranking-table">
                                    <div className="rank-title-container" style={{ backgroundImage: styleSettings.topic_title_bg_img_url ? `url(${prepareBCOSSUrl(styleSettings.topic_title_bg_img_url)})` : 'none' }}>
                                        <div className="rank-title-text">{extractTitleText()}</div>
                                    </div>
                                    <div className="table-title" style={{ color: styleSettings.bg_color || '', fontSize: '36px' }}>
                                        {getSelectedTopicName()}
                                    </div>
                                    <div className="table-header" style={{ backgroundColor: styleSettings.table_head_bg_color || '' }}>
                                        <div className="table-cell header-cell" style={{ flex: '0 0 100px' }}>
                                            实践编号
                                        </div>
                                        <div className="table-cell header-cell" style={{ flex: '1' }}>
                                            实践名称
                                        </div>
                                        <div className="table-cell header-cell" style={{ flex: '0 0 230px' }}>
                                            提报单位
                                        </div>
                                    </div>

                                    {/* 按奖项分组展示数据 */}
                                    {(dataLoading ? [] : groupDataByAward()).map((group, groupIndex) => {
                                        // 根据分组索引确定奖项等级图片
                                        let medalImage = '';
                                        if (groupIndex === 0) {
                                            medalImage = goldMedal;
                                        } else if (groupIndex === 1) {
                                            medalImage = silverMedal;
                                        } else if (groupIndex === 2) {
                                            medalImage = copperMedal;
                                        }

                                        return (
                                            <div key={groupIndex} className="award-group">
                                                {/* 奖项标题容器 */}
                                                <div className="award-title-container" style={{ backgroundImage: styleSettings.awards_bg_img_url ? `url(${prepareBCOSSUrl(styleSettings.awards_bg_img_url)})` : 'none', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    {medalImage && <img src={medalImage} alt="medal" style={{ position: 'absolute', left: '-12px', top: '-7px', width: '73px', height: '100px' }} />}
                                                    <div className="award-title-text" style={{ fontSize: 32, paddingRight: 16 }}>
                                                        {group.awardName}
                                                    </div>
                                                </div>

                                                {/* 对应奖项的数据表格 */}
                                                <div className="table-body" style={{ backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
                                                    {group.data.map(row => (
                                                        <div key={row.key} className="table-row">
                                                            <div className="table-cell" style={{ flex: '0 0 100px' }}>
                                                                {row.caseNo}
                                                            </div>
                                                            <div className="table-cell cell-content" style={{ flex: '1' }}>
                                                                {row.caseName}
                                                            </div>
                                                            <div className="table-cell cell-content" style={{ flex: '0 0 230px' }}>
                                                                {row.submissionUnit}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Drawer>

            <Modal
                title="H5榜单样式设置"
                open={styleModalVisible}
                onOk={handleStyleModalOk}
                onCancel={() => {
                    setStyleModalVisible(false);
                    setH5BackgroundUrlList([]);
                    setH5TopicTitleBgUrlList([]);
                    setH5AwardsBgUrlList([]);
                    styleForm.resetFields();
                }}
                width={800}
                zIndex={1001}
                className="export-ranking-style-modal"
            >
                <Form form={styleForm} labelCol={{ span: 5 }} wrapperCol={{ span: 19 }}>
                    {renderStyleForm()}
                </Form>
            </Modal>
        </>
    );
};

export default ExportH5RankingDrawer;
